import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Checkbox } from "@/components/ui/checkbox"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { CalendarIcon, Plus, Trash2, ChevronDown, Upload, FileText, X, File } from "lucide-react"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import { cn } from "@/lib/utils"
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { CustomFieldDefinition, CustomFieldType, CustomFieldValue } from "@/types/customer-success"

interface CustomFieldsEditorProps {
  customFields: CustomFieldValue[]
  onCustomFieldsChange: (customFields: CustomFieldValue[]) => void
}

// Função para formatar o tamanho do arquivo
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Mock data for available custom field definitions
// In a real app, this would come from an API or database
const availableCustomFields: CustomFieldDefinition[] = [
  {
    id: "field_1",
    name: "Prioridade",
    description: "Nível de prioridade da tarefa",
    field_type: "select",
    required: true,
    options: ["Baixa", "Média", "Alta", "Crítica"],
    default_value: "Média"
  },
  {
    id: "field_2",
    name: "Data limite",
    description: "Data limite para conclusão",
    field_type: "date",
    required: false
  },
  {
    id: "field_3",
    name: "Tempo estimado (horas)",
    description: "Tempo estimado para conclusão em horas",
    field_type: "number",
    required: false,
    default_value: 1
  },
  {
    id: "field_4",
    name: "Requer aprovação",
    description: "Indica se a tarefa requer aprovação",
    field_type: "boolean",
    required: false,
    default_value: false
  },
  {
    id: "field_5",
    name: "Observações",
    description: "Observações adicionais",
    field_type: "short_text",
    required: false
  },
  {
    id: "field_6",
    name: "Habilidades necessárias",
    description: "Habilidades necessárias para realizar a tarefa",
    field_type: "multiselect",
    required: false,
    options: ["Técnica", "Comunicação", "Análise", "Programação", "Design", "Gestão"]
  }
]

export default function CustomFieldsEditor({ 
  customFields, 
  onCustomFieldsChange 
}: CustomFieldsEditorProps) {
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [newFieldType, setNewFieldType] = useState<CustomFieldType>("short_text")
  const [newFieldName, setNewFieldName] = useState("")
  const [newFieldRequired, setNewFieldRequired] = useState(false)
  const [newFieldOptions, setNewFieldOptions] = useState("")
  const [newFieldDescription, setNewFieldDescription] = useState("")
  
  // Get the field definitions for the current custom fields
  const getFieldDefinition = (fieldId: string) => {
    return availableCustomFields.find(field => field.id === fieldId)
  }
  
  // Reset the new field form
  const resetNewFieldForm = () => {
    setNewFieldType("short_text")
    setNewFieldName("")
    setNewFieldRequired(false)
    setNewFieldOptions("")
    setNewFieldDescription("")
  }
  
  // Add a new custom field defined by the user
  const handleAddNewField = () => {
    if (!newFieldName.trim()) return
    
    // Generate a unique ID for the new field
    const newFieldId = `custom_${Date.now()}`
    
    // Create a new field definition
    const newFieldDefinition: CustomFieldDefinition = {
      id: newFieldId,
      name: newFieldName,
      description: newFieldDescription,
      field_type: newFieldType,
      required: newFieldRequired,
      options: newFieldType === "select" || newFieldType === "multiselect" 
        ? newFieldOptions.split(",").map(opt => opt.trim()).filter(opt => opt)
        : undefined
    }
    
    // Add the new field definition to the available fields
    availableCustomFields.push(newFieldDefinition)
    
    // Create a new field value
    const newField: CustomFieldValue = {
      field_id: newFieldId,
      value: null
    }
    
    // Add the new field to the custom fields
    onCustomFieldsChange([...customFields, newField])
    
    // Reset the form
    resetNewFieldForm()
    setShowAddDialog(false)
  }
  
  // Remove a custom field
  const handleRemoveField = (fieldId: string) => {
    onCustomFieldsChange(customFields.filter(field => field.field_id !== fieldId))
  }
  
  // Update a custom field value
  const handleUpdateFieldValue = (fieldId: string, value: any) => {
    onCustomFieldsChange(
      customFields.map(field => 
        field.field_id === fieldId 
          ? { ...field, value } 
          : field
      )
    )
  }
  
  // Render the appropriate input for each field type
  const renderFieldInput = (field: CustomFieldValue) => {
    const definition = getFieldDefinition(field.field_id)
    if (!definition) return null
    
    switch (definition.field_type) {
      case "short_text":
        return (
          <Input
            value={field.value || ""}
            onChange={(e) => handleUpdateFieldValue(field.field_id, e.target.value)}
            placeholder={`Digite ${definition.name.toLowerCase()}`}
            className="w-full"
          />
        )
        
      case "number":
        return (
          <Input
            type="number"
            value={field.value || ""}
            onChange={(e) => handleUpdateFieldValue(field.field_id, parseFloat(e.target.value) || 0)}
            placeholder={`Digite ${definition.name.toLowerCase()}`}
            className="w-full"
          />
        )
        
      case "date":
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-extrabold",
                  !field.value && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {field.value ? (
                  format(new Date(field.value), "PPP", { locale: ptBR })
                ) : (
                  <span>Selecione uma data</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={field.value ? new Date(field.value) : undefined}
                onSelect={(date) => handleUpdateFieldValue(field.field_id, date?.toISOString())}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        )
        
      case "boolean":
        return (
          <div className="flex items-center space-x-2">
            <Switch
              checked={field.value || false}
              onCheckedChange={(checked) => handleUpdateFieldValue(field.field_id, checked)}
            />
            <Label>{field.value ? "Sim" : "Não"}</Label>
          </div>
        )
        
      case "select":
        return (
          <Select
            value={field.value || ""}
            onValueChange={(value) => handleUpdateFieldValue(field.field_id, value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={`Selecione ${definition.name.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {definition.options?.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )
        
      case "multiselect":
        return (
          <div className="space-y-2">
            {definition.options?.map((option) => {
              const values = Array.isArray(field.value) ? field.value : []
              const isSelected = values.includes(option)
              
              return (
                <div key={option} className="flex items-center space-x-2">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        handleUpdateFieldValue(field.field_id, [...values, option])
                      } else {
                        handleUpdateFieldValue(
                          field.field_id,
                          values.filter((value) => value !== option)
                        )
                      }
                    }}
                  />
                  <Label>{option}</Label>
                </div>
              )
            })}
          </div>
        )
        
      case "file":
        return (
          <div className="space-y-2">
            {field.value ? (
              <div className="flex items-center justify-between p-2 border rounded-md">
                <div className="flex items-center">
                  <FileText className="h-4 w-4 mr-2" />
                  <span className="text-sm truncate max-w-[200px]">
                    {typeof field.value === 'string' 
                      ? field.value.split('/').pop() 
                      : field.value.name}
                  </span>
                  {field.value.size && (
                    <span className="text-xs text-muted-foreground ml-2">
                      ({formatFileSize(field.value.size)})
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleUpdateFieldValue(field.field_id, null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div 
                className="flex flex-col items-center justify-center p-4 border border-dashed rounded-md cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => document.getElementById(`file-upload-${field.field_id}`)?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.add('bg-muted/50');
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove('bg-muted/50');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove('bg-muted/50');
                  
                  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    const file = e.dataTransfer.files[0];
                    // Verificar tamanho do arquivo (10MB = 10 * 1024 * 1024 bytes)
                    if (file.size > 10 * 1024 * 1024) {
                      alert('O arquivo é muito grande. O tamanho máximo é 10MB.');
                      return;
                    }
                    handleUpdateFieldValue(field.field_id, file);
                  }
                }}
              >
                <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Clique ou arraste um arquivo para carregar</p>
                <p className="text-xs text-muted-foreground mt-1">Formatos aceitos: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG</p>
                <p className="text-xs text-muted-foreground">Tamanho máximo: 10MB</p>
                <input
                  id={`file-upload-${field.field_id}`}
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      const file = e.target.files[0];
                      // Verificar tamanho do arquivo (10MB = 10 * 1024 * 1024 bytes)
                      if (file.size > 10 * 1024 * 1024) {
                        alert('O arquivo é muito grande. O tamanho máximo é 10MB.');
                        return;
                      }
                      handleUpdateFieldValue(field.field_id, file);
                    }
                  }}
                />
              </div>
            )}
          </div>
        )
        
      case "files":
        return (
          <div className="space-y-2">
            <div 
              className="flex flex-col items-center justify-center p-4 border border-dashed rounded-md cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => document.getElementById(`files-upload-${field.field_id}`)?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.add('bg-muted/50');
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove('bg-muted/50');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove('bg-muted/50');
                
                if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                  const filesArray = Array.from(e.dataTransfer.files);
                  
                  // Verificar tamanho dos arquivos
                  const validFiles = filesArray.filter(file => {
                    if (file.size > 10 * 1024 * 1024) {
                      alert(`O arquivo "${file.name}" é muito grande. O tamanho máximo é 10MB.`);
                      return false;
                    }
                    return true;
                  });
                  
                  if (validFiles.length > 0) {
                    const currentFiles = Array.isArray(field.value) ? field.value : [];
                    handleUpdateFieldValue(field.field_id, [...currentFiles, ...validFiles]);
                  }
                }
              }}
            >
              <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Clique ou arraste arquivos para carregar</p>
              <p className="text-xs text-muted-foreground mt-1">Formatos aceitos: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG</p>
              <p className="text-xs text-muted-foreground">Tamanho máximo por arquivo: 10MB</p>
              <input
                id={`files-upload-${field.field_id}`}
                type="file"
                multiple
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    const filesArray = Array.from(e.target.files);
                    
                    // Verificar tamanho dos arquivos
                    const validFiles = filesArray.filter(file => {
                      if (file.size > 10 * 1024 * 1024) {
                        alert(`O arquivo "${file.name}" é muito grande. O tamanho máximo é 10MB.`);
                        return false;
                      }
                      return true;
                    });
                    
                    if (validFiles.length > 0) {
                      const currentFiles = Array.isArray(field.value) ? field.value : [];
                      handleUpdateFieldValue(field.field_id, [...currentFiles, ...validFiles]);
                    }
                  }
                }}
              />
            </div>
            
            {Array.isArray(field.value) && field.value.length > 0 && (
              <div className="space-y-2 mt-2">
                {field.value.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 border rounded-md">
                    <div className="flex items-center">
                      <File className="h-4 w-4 mr-2" />
                      <span className="text-sm truncate max-w-[200px]">
                        {typeof file === 'string' ? file.split('/').pop() : file.name}
                      </span>
                      {file.size && (
                        <span className="text-xs text-muted-foreground ml-2">
                          ({formatFileSize(file.size)})
                        </span>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const newFiles = [...field.value]
                        newFiles.splice(index, 1)
                        handleUpdateFieldValue(field.field_id, newFiles.length > 0 ? newFiles : [])
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
        
      default:
        return null
    }
  }
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-extrabold">Campos Customizados</h3>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setShowAddDialog(true)}
        >
          <Plus className="h-4 w-4 mr-1" />
          Adicionar Campo
        </Button>
      </div>
      
      {customFields.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-4 border rounded-md">
          Nenhum campo customizado adicionado
        </div>
      ) : (
        <div className="space-y-4">
          {customFields.map((field) => {
            const definition = getFieldDefinition(field.field_id)
            if (!definition) return null
            
            return (
              <div key={field.field_id} className="border rounded-md p-3 space-y-2">
                <div className="flex justify-between items-start">
                  <div>
                    <Label className="font-extrabold">{definition.name}</Label>
                    {definition.description && (
                      <p className="text-xs text-muted-foreground">{definition.description}</p>
                    )}
                    {definition.required && (
                      <span className="text-xs text-red-500 ml-1">*</span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveField(field.field_id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="pt-1">
                  {renderFieldInput(field)}
                </div>
              </div>
            )
          })}
        </div>
      )}
      
      {/* Dialog for adding new custom fields */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Adicionar Campo Customizado</DialogTitle>
            <DialogDescription>
              Defina um novo campo customizado para esta tarefa.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="field-type">Tipo de Campo</Label>
                <Select value={newFieldType} onValueChange={setNewFieldType as (value: string) => void}>
                  <SelectTrigger id="field-type">
                    <SelectValue placeholder="Selecione o tipo de campo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="short_text">Texto Curto (1 linha)</SelectItem>
                    <SelectItem value="long_text">Texto Longo (várias linhas)</SelectItem>
                    <SelectItem value="number">Número</SelectItem>
                    <SelectItem value="date">Data</SelectItem>
                    <SelectItem value="boolean">Sim/Não</SelectItem>
                    <SelectItem value="select">Lista de Seleção</SelectItem>
                    <SelectItem value="multiselect">Lista de Múltipla Seleção</SelectItem>
                    <SelectItem value="file">Arquivo (único)</SelectItem>
                    <SelectItem value="files">Arquivos (múltiplos)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {newFieldType === "short_text" && "Campo para entrada de texto curto em uma única linha."}
                  {newFieldType === "long_text" && "Campo para entrada de texto longo com múltiplas linhas."}
                  {newFieldType === "number" && "Campo para entrada de valores numéricos."}
                  {newFieldType === "date" && "Campo para seleção de data em um calendário."}
                  {newFieldType === "boolean" && "Campo para seleção de Sim ou Não."}
                  {newFieldType === "select" && "Campo para seleção de uma única opção de uma lista."}
                  {newFieldType === "multiselect" && "Campo para seleção de múltiplas opções de uma lista."}
                  {newFieldType === "file" && "Campo para upload de um único arquivo."}
                  {newFieldType === "files" && "Campo para upload de múltiplos arquivos."}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="field-name">Nome do Campo</Label>
                <Input 
                  id="field-name" 
                  value={newFieldName} 
                  onChange={(e) => setNewFieldName(e.target.value)}
                  placeholder="Ex: Prioridade"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="field-description">Descrição (opcional)</Label>
                <Textarea 
                  id="field-description" 
                  value={newFieldDescription} 
                  onChange={(e) => setNewFieldDescription(e.target.value)}
                  placeholder="Descreva o propósito deste campo"
                  rows={2}
                />
              </div>
              
              {(newFieldType === "select" || newFieldType === "multiselect") && (
                <div className="space-y-2">
                  <Label htmlFor="field-options">Opções (separadas por vírgula)</Label>
                  <Textarea 
                    id="field-options" 
                    value={newFieldOptions} 
                    onChange={(e) => setNewFieldOptions(e.target.value)}
                    placeholder="Ex: Baixa, Média, Alta, Crítica"
                    rows={2}
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Obrigatoriedade</Label>
                <div className="flex items-center justify-between">
                  <Label htmlFor="field-required" className="text-sm text-muted-foreground">Campo obrigatório</Label>
                  <Switch
                    id="field-required" 
                    checked={newFieldRequired}
                    onCheckedChange={(checked) => setNewFieldRequired(checked)}
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              resetNewFieldForm()
              setShowAddDialog(false)
            }}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAddNewField} 
              disabled={!newFieldName.trim() || (["select", "multiselect"].includes(newFieldType) && !newFieldOptions.trim())}
            >
              Adicionar Campo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
} 